import { GridSortItem } from "@mui/x-data-grid";
import dayjs, { Dayjs } from "dayjs";

const serverUrl = "https://casemanagement.howellbuchanandstrong.com:12345";

const wssUrl = "wss://casemanagement.howellbuchanandstrong.com";

export const tempDocsUrl =
	"https://casemanagement.howellbuchanandstrong.com/temp-docs/";

export enum QueryStatus {
	IDLE,
	LOADING,
	SUCCESS,
	FAILURE,
}

export enum SessionStatus {
	LOGGED_OUT,
	LOGGED_IN,
	EXPIRED,
}

export interface DocumentFormField {
	label: string;
	type: "text" | "text-long" | "date" | "email" | "phone" | "money" | "choice";
	format?: string;
	desc?: string;
	choices?: string[];
}

export interface DocumentFormData {
	name: string;
	filename: string;
	fields: DocumentFormField[];
}

export interface Detail {
	_id: string;
	date: string;
	type: string;
	caseId: string;
	assignees: string[];
	description: string;
	isComplete?: boolean;
}

export interface Person {
	_id: string;
	archived: boolean;
	name: string;
	email: string;
	roles: string[];
}

export interface Exemption {
	_id: string;
	application: string;
	status: string;
	submitted: string;
}

export interface DetailNotification {
	_id: string;
	detailId: string;
	dayOffset: number;
	archived: boolean;
}

export interface LayoutField {
	label: string;
	type: "text" | "choice" | "email" | "phone" | "subform" | "date";
	choices?: string[];
	template?: LayoutField[];
	minimum?: number;
}

export interface SessionState {
	status: SessionStatus;
	token?: string;
}

export interface LayoutSettings {
	[id: string]: {
		overrides?: { [id: string]: string };
		template: (LayoutField | null)[];
	};
}

export interface SettingsObject {
	_id?: string;
	caseTypes: string[];
	accountTypes: string[];
	drmOtherTypes: string[];
	statusTypes: string[];
	layoutByCaseType: LayoutSettings;
	exemptionApplicationTypes: string[];
	exemptionStatusTypes: string[];
}

export interface CommonData {
	caseType: string;
	attorneys: string[];
	secondaryAttorneys: string[];
	accountType: string;
	DRMOther: string;
	staffs: string[];
	secondaryStaffs: string[];
	caseOpenDate: Dayjs | null;
	lastUpdate: Dayjs | null;
	status: string;
	caseNumber: string;
}

function wrapSignature(content: object, sessionState: SessionState) {
	return JSON.stringify({
		content,
		token: sessionState.token,
	});
}

export function createDocumentSocket(
	filename: string,
	handler: (action: string, payload: any) => void,
	sessionState: SessionState
) {
	const socket = new WebSocket(wssUrl);
	socket.addEventListener("open", () => {
		postDocumentSocket(socket, "START_DOC", { filename }, sessionState);
	});
	socket.addEventListener("message", (event) => {
		const { action, payload } = JSON.parse(event.data);
		handler(action, payload);
	});
	return socket;
}

export function postDocumentSocket(
	socket: WebSocket,
	action: string,
	payload: object,
	sessionState: SessionState
) {
	socket.send(wrapSignature({ action, payload }, sessionState));
}

export async function getData(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void,
	miscSearch?: string,
	people?: Person[]
) {
	try {
		const content = {
			action: "get-case-list",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		if (miscSearch === undefined || people === undefined) {
			return res;
		}
		return res.filter((datum: { [id: string]: any }) => {
			if (miscSearch !== "") {
				for (const [key, value] of Object.entries(datum)) {
					if (["_id", "archived", "status"].includes(key)) {
						continue;
					} else if (
						[
							"attorneys",
							"secondaryAttorneys",
							"staffs",
							"secondaryStaffs",
						].includes(key)
					) {
						for (const id of value) {
							if (
								(
									people.find(({ _id }) => id === _id)
										?.name || ""
								)
									.toLowerCase()
									.includes(miscSearch.toLowerCase())
							) {
								return true;
							}
						}
					} else if (
						(value || "")
							.toLowerCase()
							.includes(miscSearch.toLowerCase())
					) {
						return true;
					}
				}
				return false;
			}
			return true;
		});
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getCasesByIds(
	ids: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-cases-by-ids",
			ids,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getDeadlines(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-deadlines",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getNotes(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-notes",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getExemptions(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-exemptions",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getDetails(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-details",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getCase(
	id: string,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-case",
			id,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getSettings(
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-settings",
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res;
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getPeople(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-people",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res.sort((a: Person, b: Person) => a.name.localeCompare(b.name));
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export async function getNotifications(
	query: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void
) {
	try {
		const content = {
			action: "get-notifications",
			query,
		};
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		return res.sort(
			(a: DetailNotification, b: DetailNotification) =>
				a.dayOffset - b.dayOffset
		);
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		} else {
			throw e;
		}
	}
}

export function removeDuplicates(a: any[]) {
	return Array.from(new Set(a));
}

export function stringArraysEqual(a: string[], b: string[]) {
	if (a.length !== b.length) {
		return false;
	}

	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) {
			return false;
		}
	}

	return true;
}

export async function postAction(
	content: object,
	sessionState: SessionState,
	setSessionState: (arg0: SessionState) => void,
	onSuccess?: (arg0: { [id: string]: any }) => void,
	onFailure?: (e: any) => void
) {
	try {
		const res = await (
			await fetch(serverUrl, {
				method: "post",
				body: wrapSignature(content, sessionState),
			})
		).json();
		if (res?.status === "Failure") {
			throw res?.reason;
		}
		if (onSuccess) {
			onSuccess(res);
		}
	} catch (e) {
		if (e === "Expired Token") {
			setSessionState({
				status: SessionStatus.EXPIRED,
			});
		}

		if (onFailure) {
			onFailure(e);
		} else {
			throw e;
		}
	}
}

export function compareDates(a: Dayjs | null, b: Dayjs | null) {
	if (a?.isValid() && b?.isValid()) {
		return a?.toISOString() === b?.toISOString();
	}
	if (a === null && b === null) {
		return true;
	}
	return false;
}

export function sortCaseListData(
	sortModel: GridSortItem[],
	data: object[],
	deadlines: { [id: string]: string }
) {
	let sortedData = [...data];
	for (let e of sortModel) {
		switch (e.field) {
			case "clientName":
				switch (e.sort) {
					case "asc":
						sortedData.sort((a: any, b: any) =>
							(
								a["Client Name$"] ||
								a["Client Name$_0"] ||
								""
							).localeCompare(
								b["Client Name$"] || b["Client Name$_0"] || ""
							)
						);
						break;
					case "desc":
						sortedData.sort(
							(a: any, b: any) =>
								-(
									a["Client Name$"] ||
									a["Client Name$_0"] ||
									""
								).localeCompare(
									b["Client Name$"] ||
										b["Client Name$_0"] ||
										""
								)
						);
						break;
				}
				break;
			case "attorney":
				switch (e.sort) {
					case "asc":
						sortedData.sort((a: any, b: any) =>
							a["attorney"] < b["attorney"]
								? -1
								: a["attorney"] === b["attorney"]
								? 0
								: 1
						);
						break;
					case "desc":
						sortedData.sort((a: any, b: any) =>
							a["attorney"] < b["attorney"]
								? 1
								: a["attorney"] === b["attorney"]
								? 0
								: -1
						);
						break;
				}
				break;
			case "caseStatus":
				switch (e.sort) {
					case "asc":
						sortedData.sort((a: any, b: any) =>
							a["status"] < b["status"]
								? -1
								: a["status"] === b["status"]
								? 0
								: 1
						);
						break;
					case "desc":
						sortedData.sort((a: any, b: any) =>
							a["status"] < b["status"]
								? 1
								: a["status"] === b["status"]
								? 0
								: -1
						);
						break;
				}
				break;
			case "caseType":
				switch (e.sort) {
					case "asc":
						sortedData.sort((a: any, b: any) =>
							a["caseType"] < b["caseType"]
								? -1
								: a["caseType"] === b["caseType"]
								? 0
								: 1
						);
						break;
					case "desc":
						sortedData.sort((a: any, b: any) =>
							a["caseType"] < b["caseType"]
								? 1
								: a["caseType"] === b["caseType"]
								? 0
								: -1
						);
						break;
				}
				break;
			case "staff":
				switch (e.sort) {
					case "asc":
						sortedData.sort((a: any, b: any) =>
							a["staff"] < b["staff"]
								? -1
								: a["staff"] === b["staff"]
								? 0
								: 1
						);
						break;
					case "desc":
						sortedData.sort((a: any, b: any) =>
							a["staff"] < b["staff"]
								? 1
								: a["staff"] === b["staff"]
								? 0
								: -1
						);
						break;
				}
				break;
			case "deadline":
				switch (e.sort) {
					case "asc":
						sortedData.sort((a: any, b: any) => {
							if (
								deadlines[a["_id"]] === undefined &&
								deadlines[b["_id"]] === undefined
							) {
								return 0;
							}
							if (deadlines[a["_id"]] === undefined) {
								return -1;
							}
							if (deadlines[b["_id"]] === undefined) {
								return 1;
							}
							return dayjs(deadlines[b["_id"]]).diff(
								dayjs(deadlines[a["_id"]])
							);
						});
						break;
					case "desc":
						sortedData.sort((a: any, b: any) => {
							if (
								deadlines[a["_id"]] === undefined &&
								deadlines[b["_id"]] === undefined
							) {
								return 0;
							}
							if (deadlines[a["_id"]] === undefined) {
								return 1;
							}
							if (deadlines[b["_id"]] === undefined) {
								return -1;
							}
							return -dayjs(deadlines[b["_id"]]).diff(
								dayjs(deadlines[a["_id"]])
							);
						});
						break;
				}
				break;
		}
	}
	return sortedData;
}
